import Image from 'next/image';

import { Box, Card, Flex, Heading, LinkBox, LinkOverlay, Tag, Text } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { AccreditationColorScheme, AccreditationNewsItem } from '~/features/accreditation/types';
import { trackEvent } from '~/features/analytics/track-event';
import { FormalDegreeKey } from '~/features/checkout/constants';
import { ArrowRightIcon, DocumentIcon, MicrophoneIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';

export function AccreditationNewsSection({
  colorScheme = 'blue',
  items,
  formalDegreeKey,
}: {
  colorScheme?: AccreditationColorScheme;
  items: AccreditationNewsItem[];
  formalDegreeKey?: FormalDegreeKey;
}) {
  if (!items || items.length === 0) return null;

  return (
    <ContentContainer>
      <Card
        as='section'
        border='1px solid'
        borderColor='grey.200'
        borderRadius='12px'
        boxShadow='none'
        display='flex'
        flexDirection='column'
        gap={2}
        maxWidth='1056px'
        mx='auto'
        overflow='hidden'
        position='relative'
        padding={{
          base: '24px 16px',
          md: '40px 48px',
        }}
      >
        <Box height='307px' position='absolute' right={0} top={0} width='307px'>
          <Image
            alt=''
            sizes='307px'
            src={`/images/accreditation/bg-stripes-${colorScheme === 'blue' ? 'blue' : 'crimson'}.png`}
            style={{ objectFit: 'cover', pointerEvents: 'none' }}
            fill
          />
        </Box>
        <Heading
          as='h2'
          borderBottom='1px solid'
          borderBottomColor='grey.200'
          color={`${colorScheme}.500`}
          fontSize={{ base: '1.75rem', md: '2.5rem' }}
          fontWeight='600'
          paddingBottom={6}
          position='relative'
          lineHeight={{
            base: '1.14',
            md: '1.1',
          }}
        >
          Udacity in the News
        </Heading>

        <Flex flexDirection='column' gap='16px' position='relative'>
          {items.map((item, index) => (
            <NewsCard key={index} colorScheme={colorScheme} formalDegreeKey={formalDegreeKey} item={item} />
          ))}
        </Flex>
      </Card>
    </ContentContainer>
  );
}

function NewsCard({
  colorScheme,
  item,
  formalDegreeKey,
}: {
  colorScheme: AccreditationColorScheme;
  item: AccreditationNewsItem;
  formalDegreeKey?: FormalDegreeKey;
}) {
  return (
    <Flex
      alignItems='center'
      as={LinkBox}
      direction={{ base: 'column', md: 'row' }}
      gap={{ base: '12px', md: '24px' }}
      _notFirst={{
        borderTop: '1px solid',
        borderTopColor: 'grey.100',
      }}
      padding={{
        base: '16px 0px',
        md: '24px 16px',
      }}
      onClick={() =>
        trackEvent({
          name: 'Masters Page CTA Clicked',
          type: 'click',
          label: 'news_clicked',
          value: item.title,
          additionalAttributes: {
            formal_degree_key: formalDegreeKey,
          },
        })
      }
    >
      <Flex flex='1' flexDirection='column' gap='8px'>
        <Heading
          alignItems='flex-start'
          as='p'
          display='flex'
          flexDirection='column'
          fontWeight='600'
          gap={2}
          lineHeight='1.2'
          fontSize={{
            base: '1.125rem',
            md: '1.25rem',
          }}
        >
          <Tag
            alignItems='center'
            alignSelf='flex-start'
            bg={`${colorScheme}.100`}
            border='none'
            borderRadius='4px'
            color={`${colorScheme}.700`}
            display='flex'
            flexShrink={0}
            fontFamily='condensed'
            fontSize='0.875rem'
            fontWeight='400'
            gap={2}
            letterSpacing='0.08em'
            lineHeight='1'
            px='10px'
            py='5px'
            textTransform='uppercase'
            whiteSpace='nowrap'
          >
            {item.type === 'podcast' && <MicrophoneIcon flexShrink={0} height='14px' width='14px' />}
            {item.type === 'article' && <DocumentIcon flexShrink={0} height='14px' width='14px' />}
            {item.tag}
          </Tag>
          <LinkOverlay href={item.href} isExternal>
            {item.title}
          </LinkOverlay>
        </Heading>
        <Text color='grey.500' fontSize='0.8125rem' lineHeight='1.4'>
          {item.description}
        </Text>
      </Flex>
      <ArrowRightIcon color='grey.900' display={{ base: 'none', md: 'block' }} h='20px' w='20px' />
    </Flex>
  );
}
